import i18next from "i18next";

export class PathResolver {
    constructor() {
        this.windMap = {};
        this.itemsMap = {};
        this.objInsMap = {};
        this.createObjectTree();
    }

    getWindowIdByPath(path) {
        const winId = this.getWindowIdByPathRecursive(this.objTree, path,
            [i18next.t("pathresolver_resources"), i18next.t("pathresolver_graphic_user_interface")],
            i18next.t("pathresolver_windows"));
        if (!winId) {
            $ns.add({
                type: "warning",
                time: new Date().toLocaleString(),
                title: i18next.t("title_window_not_found"),
                text: String.format(i18next.t("window_not_found_path"), path),
            });
            return;
        }
        return winId;
    }

    getWindowIdByPathRecursive(node, path, excludedParts, windowsNode) {
        if (typeof node[path] !== "undefined") {
            return node[path];
        }
        if (node[windowsNode] && typeof node[windowsNode][path] !== "undefined") {
            return node[windowsNode][path];
        }

        let paths = path.split(".");
        const part = paths.shift();
        if (!paths.length) {
            return node[windowsNode] && typeof node[windowsNode][part] !== "undefined"
                ? node[windowsNode][part]
                : null;
        }

        if (excludedParts.indexOf(part) !== -1) {
            return this.getWindowIdByPathRecursive(node, paths.join("."), excludedParts, windowsNode);
        }
        else {
            if (typeof node[part] !== "undefined") {
                const res = this.getWindowIdByPathRecursive(node[part], paths.join("."), excludedParts, windowsNode);
                if (res)
                    return res;
            }
            if (node[windowsNode] && typeof node[windowsNode][part] !== "undefined") {
                return this.getWindowIdByPathRecursive(node[windowsNode][part], paths.join("."), excludedParts, windowsNode);
            }
            return null;            
        }
    }

    getObjectByPath(objPath) {
        let pathParts = objPath.split(".");
        let node = this.objTree;
        for (const part of pathParts) {
            if (part === i18next.t("pathresolver_resources") || part === i18next.t("pathresolver_windows")) {
                continue;
            };

            let child = node[part];
            if (typeof child === "undefined") {
                return null;
            };
            node = child;
        }
        return node;
    }

    getObjectById(id) {
        return this.itemsMap[id];
    }

    getLuaName(path) {
        let arr = this.screenInfo.projectTree;
        const paths = path.split(".");
        for (let part of paths) {
            const node = arr?.find(({ Name }) => Name === part);
            if (!node)
                return null;
            if (node.LuaName) {
                return node.LuaName;
            }
            arr = node.Childs;
        }
    }

    getWindowIdByName(winName, objId) {
        let objWinds = this.windMap[objId];
        if (typeof objWinds !== "undefined") {
            return winName.split(".").reduce((acc, val) => acc ? acc[val] : null, objWinds);
        }
    }

    getNodeFullName(node) {
        let res = node._Name;
        let p = node._Parent;
        while (p !== this.objTree) {
            res = p._Name + "." + res;
            p = p._Parent;
        }
        return res;
    }

    getFullNameIfInInstance(objId) {
        let obj = this.itemsMap[objId];
        if (typeof obj !== "undefined") {
            let p = obj._Parent;
            while (p !== this.objTree) {
                if (p._Type == "ObjectInstance") {
                    return this.getNodeFullName(obj);
                }
                p = p._Parent;
            }
        }
        return null;
    }

    getFullName(objId) {
        let obj = this.itemsMap[objId];
        if (typeof obj !== "undefined") {
            return this.getNodeFullName(obj);            
        }
        return null;
    }

    getFullNameByLuaName(path, outerpath) {      
        let paths = path.split(".");
        let node = this.objTree;
        let name = "";

        for (let i = 0; i < paths.length; i++) {
            const part = paths[i];
            if (part === i18next.t("pathresolver_resources")) continue;
            let child = node[part];
            if (typeof child === "undefined") return "";
            node = child;
        }

        let outerpaths = outerpath.split(".").filter(Boolean);
        for (let j = 0; j < outerpaths.length; j++) {
            const outpart = outerpaths[j];
            let { nextNode, addedName } = this.getNameByLuaNameRecursive(node, outpart);           
            if (addedName === "") {
                break;
            }
            node = nextNode;
            name += addedName;
        }
        return name;
    }

    getNameByLuaNameRecursive(node, outpart, prevName = "") {        
        for (let child in node) {
            if (node[child] && node[child]._LuaName === outpart) {
                node = node[child];
                return { nextNode: node, addedName: prevName + "." + node._Name };
            }
        }        
        for (let child in node) {
            if (node[child] && child != "_Parent" && node[child]._Name) {
                let { nextNode, addedName } = this.getNameByLuaNameRecursive(node[child], outpart, prevName + "." + node[child]._Name);
                if (addedName != "") {
                    return { nextNode: nextNode, addedName: addedName };
                }                
            }
        }
        
        return { nextNode: node, addedName: ""};
    }   

    getObjectIdByPath(path) {
        let paths = path.split(".");
        let node = this.objTree;
        let objId = 0;
        let outerpath = "";
        for (let i = 0; i < paths.length; i++) {
            const part = paths[i];
            if (part === i18next.t("pathresolver_resources")) continue;
            let child = node[part];
            if (typeof child === "undefined") break;

            if (
                child._Type === "ObjectInstance" &&
                typeof child._ID !== "undefined"
            ) {
                if (objId === 0) objId = child._ID;
                else if (child._LuaName) outerpath += child._LuaName + ".";
            }
            node = child;
        }
        return { objectid: objId, outerpath: outerpath };
    }

    async createObjectTree() {
        this.objTree = {};
        this.vmInfo = await this.loadJSON("VMInfo.json");
        this.addObjTreeData(this.vmInfo.projectTree, "ObjectRoot");
        this.addObjTreeData(this.vmInfo.projectTree, "SystemRoot");

        if (_globalParams.UseScreenInfo) {
            await this.mergeScreenInfo();
        }
    }

    async mergeScreenInfo() {
        // файл "ScreenInfo.json" будет только при наличии задачи экрана
        this.screenInfo = await this.loadJSON("ScreenInfo.json");
        const objectRootTree = this.screenInfo.projectTree.find(obj => obj.TranslitedName === 'ObjectRoot');
        if (objectRootTree?.Childs?.length) {
            const initialObj = this.objTree[objectRootTree.Name];

            const searchNewData = (sourceObj, childs) => {
                childs.forEach(newObj => {
                    const newSourceObj = sourceObj[newObj.Name];
                    if (newSourceObj) {
                        searchNewData(newSourceObj, newObj.Childs);
                    } else {
                        sourceObj[newObj.Name] = this.createItem(newObj, $WinDefs.winMap, sourceObj)
                    }
                });
            }

            if (initialObj)
                searchNewData(initialObj, objectRootTree.Childs);
        }
    }


    addObjTreeData(projectTree, TranslitedName) {
        const objTree = projectTree.find(obj => obj.TranslitedName === TranslitedName);
        if (objTree) {
            this.objTree[objTree.Name] = this.createItem(objTree, $WinDefs.winMap, this.objTree);
        }
    }

    getChilds(treeEl, windMap, parent) {
        if (treeEl?.Childs?.length) {
            for (let child of treeEl.Childs) {
                //const type = child.Type;
                //if (type === "Object" || type === "ObjectInstance" || type === "Controller" || type === "Folder" || type === "Protocol") {
                    parent[child.Name] = this.createItem(child, windMap, parent);
                //}
            }
        }
    }

    createItem(element, windMap, parent) {
        let result = {
            _ID: element.ID,
            _Name: element.Name,
            _Description: element.Description,
            _Type: element.Type,
            _STType: element.STType,
            _Parent: parent,
            _DefaultWindowID: element.DefaultWindowID,
            _IncludeInNavigationControls: element.IncludeInNavigationControls
        };
        this.itemsMap[element.ID] = result;
        if (element.LuaName) result._LuaName = element.LuaName;
        if (element.Type === "Object" || element.Type === "Controller" || element.Type === "Folder" || element.Type === "Protocol") {
            if (typeof windMap[element.ID] !== "undefined") {
                let tempMap = windMap[element.ID];
                this.windMap[element.ID] = { ...tempMap };
                result[i18next.t("pathresolver_windows")] = { ...tempMap };
            }
        } else if (element.Type === "ObjectInstance") {
            if (typeof this.vmInfo.types !== "undefined") {
                let type = this.vmInfo.types.find(
                    x => x.Name === element.STType
                );
                this.objInsMap[type.ID] ??= [];
                this.objInsMap[type.ID].push(element.ID);
                let tempMap = type.Windows;
                this.windMap[element.ID] = { ...tempMap };
                result[i18next.t("pathresolver_windows")] = { ...tempMap };
            }
        }
        this.getChilds(element, windMap, result);

        return result;
    }

    loadJSON(path) {
        return new Promise(resolve => {
            const xobj = new XMLHttpRequest();
            xobj.overrideMimeType("application/json");
            xobj.open("GET", path, true);
            xobj.onreadystatechange = function () {
                if (xobj.readyState === 4 && xobj.status === 200) {
                    try {
                        resolve(JSON.parse(xobj.responseText));
                    } catch (e) { }
                }
            };
            xobj.send(null);
        });
    }

    getFirstWindow(objWins, defaultWinId) {
        for (const win in objWins) {
            const winid = objWins[win];
            if (winid) {
                if (!isNaN(+winid) && !$WinDefs.screenMap[winid]
                    && (!defaultWinId || defaultWinId == winid))
                    return win;
                if (typeof winid === "object") {
                    const innerwin = this.getFirstWindow(winid, defaultWinId);
                    if (innerwin)
                        return `${win}.${innerwin}`;
                }
            }
        }
        return null;
    }
}
